import { SignAssociation } from 'src/app/components/stock/sign-associations/edit-association/sign-association';
import { SignAssociationAsset } from 'src/app/components/stock/sign-associations/edit-association/sign-association-asset';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ContactHelper } from 'src/app/helpers/contact-helper';
import DateHelper from 'src/app/helpers/date-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import { FileHelper } from 'src/app/helpers/file-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardAssetHelper } from 'src/app/helpers/ip-board-asset-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { KeysOfType } from 'src/app/helpers/utility-functions';
import Asset from 'src/app/model/asset';
import LoggedAction from 'src/app/model/logged-action';
import ProductAsset from 'src/app/model/product-asset';
import BoardType from 'src/app/model/product/board/board-type';
import PlacementType from 'src/app/model/product/board/placement-type';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import IPBoardPlacement from 'src/app/model/product/ip-board/ip-board-placement';
import { IPBoardPlacementTypeChoices } from 'src/app/model/product/ip-board/ip-board-placement-type';
import Product from 'src/app/model/product/product';
import Address from 'src/app/model/project/address';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import { SoldRented } from 'src/app/model/project/sold-rented';
import {
  SUB_PROJECT_TYPES,
  SubProject,
} from 'src/app/model/project/sub-project';
import { REGISTERED_ACTION_TYPES } from 'src/app/model/registered-action-type';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import { User } from 'src/app/model/user';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { DisplayDateTimePipe } from 'src/app/pipes/display-date-time.pipe';
import {
  GroupProjectContactPipe,
  ProjectContactGroup,
} from 'src/app/pipes/group-project-contact';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { ImmoIPChangesService } from 'src/app/services/immo-ip-changes/immo-ip-changes-service';
import { ImmoIPNotExecutedHistoryItemObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { ToDoItem } from 'src/app/services/immo-ip-placer-offline-changes/model/to-do-item';
import { ClientSignSelectorHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/client-sign-selector/client-sign-selector-helper';
import { ClientIPBoardAndPlacementHelper } from 'src/app/shared/project-components/client-project-form/base-project/client-ip-board-form/helper/client-ip-board-and-placement-helper';
import { TourDetailProjectPlacement } from '../../../tour/tour-page/employee-tour-edit-modal/helper/model/tour-detail-project-placement';
import { ImmoIPPlacerTourViewHelper } from '../../tour-view/helper/immo-ip-placer-tour-view-helper';
import { OfflineStorageTourHelper } from '../../tour-view/offline-storage/helper/offline-storage-tour-helper';
import { ChangeType } from '../../tour-view/offline-storage/offline-storage-tour.service';
import {
  ImmoIPPlacerTourItemDetailChangeStorageHandler,
  ProductExecutedParameterObject,
} from './immo-ip-placer-tour-item-detail-change-storage-handler';
import {
  TourItemDetailAsset,
  TourItemDetailProductWrapper,
} from './model/tour-item-detail-product-wrapper';
import { TourItemDetailSoldRentedStickerInfo } from './model/tour-item-detail-sold-rented-sticker-info';

export class ImmoIPPlacerTourItemDetailHelper {
  private constructor() {}

  static processSubProject(subProject: SubProject): void {
    ImmoIPPlacerTourViewHelper.setTypeDisplays(subProject);
  }

  static getProjectContactGroups(
    subProject: SubProject,
    groupProjectContacts: GroupProjectContactPipe,
    formatPhoneNumber: FormatPhoneNumber
  ): ProjectContactGroup[] {
    const toReturn = groupProjectContacts.transform(
      subProject.project == null ? null : subProject.project.projectContacts
    );
    if (CollectionHelper.isPopulated(toReturn)) {
      toReturn.forEach((pcg) => {
        if (CollectionHelper.isPopulated(pcg.contacts)) {
          pcg.contacts.forEach((c) =>
            ContactHelper.setFormDisplays(c, formatPhoneNumber)
          );
        }
      });
    }
    return toReturn;
  }

  static getTimeBudgetDisplay(subProject: SubProject): string {
    if (StringHelper.isEmpty(subProject.type)) {
      return null;
    } else if (
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT ||
      subProject.type === SUB_PROJECT_TYPES.REPO
    ) {
      return this.getBaseProjectOrRepoTimeBudgetDisplay(
        subProject,
        subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      );
    } else if (
      subProject.type === SUB_PROJECT_TYPES.ADJUSTMENT ||
      subProject.type === SUB_PROJECT_TYPES.SOLDRENTED
    ) {
      return this.getAdjustmentOrSoldRentedTimeBudgetDisplay(subProject);
    }
  }

  private static getBaseProjectOrRepoTimeBudgetDisplay(
    subProject: SubProject,
    placement: boolean
  ): string {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return null;
    }
    let sum = 0;
    subProject.project.products.forEach((p) => {
      if (!ProductHelper.isIPBoard(p)) {
        return;
      }
      const boardPart = (<IPBoard>p).boardPart;
      const toAdd = placement
        ? boardPart.placementMinuteBudget
        : boardPart.repoMinuteBudget;
      sum += toAdd == null ? 0 : toAdd;
    });
    return sum <= 0 ? null : `${sum} min.`;
  }

  private static getAdjustmentOrSoldRentedTimeBudgetDisplay(
    subProject: SubProject
  ): string {
    const budget = ProjectHelper.getTimeBudgetMinutes(subProject);
    return budget <= 0 ? null : `${budget} min.`;
  }

  static getAppointmentInfo(
    subProject: SubProject,
    printAddressPipe: PrintAddressPipe,
    dateTimePipe: DisplayDateTimePipe,
    formatPhoneNumber: FormatPhoneNumber
  ): AppointmentInfo {
    if (
      subProject.projectPlacement == null ||
      !(
        subProject.projectPlacement.appointment ||
        subProject.projectPlacement.collectKey
      )
    ) {
      return null;
    }

    let appointmentTimeOfDay = null;
    let appointmentContactDisplay = null;
    let collectKeyAddress = null;
    let appointmentRegistration = null;
    if (subProject.projectPlacement.appointment) {
      const appointmentTime = <string>(
        (<unknown>subProject.projectPlacement.appointmentTime)
      );
      appointmentTimeOfDay = StringHelper.isEmpty(appointmentTime)
        ? subProject.projectPlacement.appointmentTimeOfDay
        : appointmentTime.slice(0, 5);
      appointmentContactDisplay = this.getAppointmentContactDisplay(
        subProject.projectPlacement,
        formatPhoneNumber
      );

      appointmentRegistration =
        subProject.projectPlacement.appointmentReadyRegistration != null
          ? this.getAppointmentRegistration(
              subProject.projectPlacement.appointmentReadyRegistration,
              dateTimePipe
            )
          : subProject.projectPlacement.appointmentFailureRegistration != null
          ? this.getAppointmentRegistration(
              subProject.projectPlacement.appointmentFailureRegistration,
              dateTimePipe
            )
          : null;
    }

    if (subProject.projectPlacement.collectKey) {
      if (subProject.project) {
        collectKeyAddress = ProjectHelper.calculateCollectKeyFromAddress(
          subProject,
          printAddressPipe
        );
        if (subProject.projectPlacement.appointmentContact != null) {
          collectKeyAddress = StringHelper.join(
            [
              subProject.projectPlacement.appointmentContact.firstName,
              collectKeyAddress,
            ],
            ', '
          );
        }
      }
    }
    return {
      appointmentTimeOfDay: appointmentTimeOfDay,
      appointmentContactDisplay: appointmentContactDisplay,
      needAppointmentInfo: subProject.projectPlacement.needAppointmentInfo,
      appointmentReady: subProject.projectPlacement.appointmentReady,
      appointmentFailure: subProject.projectPlacement.appointmentFailure,
      appointmentRegistration: appointmentRegistration,
      appointmentWithCustomer:
        subProject.projectPlacement.appointmentWithCustomer,
      collectKeyAddress: collectKeyAddress,
    };
  }

  static getAppointmentContactDisplay(
    projectPlacement: ProjectPlacement | TourDetailProjectPlacement,
    formatPhoneNumber: FormatPhoneNumber
  ): string {
    if (projectPlacement.appointmentContact != null) {
      return ContactHelper.getContactDisplay(
        projectPlacement.appointmentContact,
        formatPhoneNumber
      );
    } else {
      return (
        ContactHelper.displayIfNotEmpty(projectPlacement.appointmentName) +
        ContactHelper.displayIfNotEmpty(
          projectPlacement.appointmentPhoneNumber
        ) +
        ContactHelper.displayIfNotEmpty(projectPlacement.appointmentEmail, true)
      );
    }
  }

  static getAppointmentRegistration(
    loggedAction: LoggedAction,
    dateTimePipe: DisplayDateTimePipe
  ): string {
    let appointmentRegistration = 'Geregistreerd';
    if (loggedAction.by != null) {
      appointmentRegistration +=
        ' door ' + loggedAction.by.firstName + ' ' + loggedAction.by.lastName;
    }
    if (loggedAction.date != null) {
      appointmentRegistration +=
        ' op ' + dateTimePipe.transform(loggedAction.date, loggedAction.date);
    }
    return appointmentRegistration;
  }

  static processProducts(
    baseProject: boolean,
    collaborative: boolean,
    products: Product[],
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    placementFloor: string
  ): TourItemDetailProductWrapper[] {
    if (!CollectionHelper.isPopulated(products)) {
      return null;
    }

    const toReturn: TourItemDetailProductWrapper[] = products.map((p) => {
      if (ProductHelper.isIPBoard(p)) {
        const wrapper: TourItemDetailProductWrapper = {
          ipBoard: <IPBoard>p,
        };
        this.processIPBoard(
          baseProject,
          collaborative,
          <IPBoard>p,
          wrapper,
          immoIPPlacerTourItemDetailChangeStorageHandler,
          placementFloor
        );
        if (baseProject && p.immoscoopLogo) {
          wrapper.immoscoop = {
            label: 'Immoscoop',
            done: false,
          };
          this.fillToDoItem(
            p,
            wrapper.immoscoop,
            immoIPPlacerTourItemDetailChangeStorageHandler,
            'immoscoop'
          );
        }
        if (baseProject && p.spottoLogo) {
          wrapper.spotto = {
            label: 'Spotto',
            done: false,
          };
          this.fillToDoItem(
            p,
            wrapper.spotto,
            immoIPPlacerTourItemDetailChangeStorageHandler,
            'spotto'
          );
        }
        return wrapper;
      } else {
        return null;
      }
    });
    CollectionHelper.filterInPlace(toReturn, (e) => e != null);
    return toReturn;
  }

  private static fillToDoItem(
    product: Product,
    toDoItem: ToDoItem,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    prop: string
  ): void {
    if (
      product.productPlacement != null &&
      product.productPlacement.executionComplete
    ) {
      toDoItem.done = true;
    } else {
      immoIPPlacerTourItemDetailChangeStorageHandler.fillProductToDoItem(
        product.id,
        toDoItem,
        prop
      );
    }
  }

  private static processIPBoard(
    baseProject: boolean,
    collaborative,
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    placementFloor: string
  ): void {
    if (ipBoard.boardPart == null) {
      return;
    }

    if (ipBoard.boardPart.sign != null) {
      this.processSignIPBoardPart(ipBoard, wrapper);
    } else {
      this.processCustomIPBoardPart(ipBoard, collaborative, wrapper);
    }

    this.processIPBoardPartPlacementType(
      baseProject,
      ipBoard,
      wrapper,
      immoIPPlacerTourItemDetailChangeStorageHandler
    );
    this.processDesignAssets(ipBoard, wrapper);
    this.processPoleColor(ipBoard, wrapper);
    if (baseProject) {
      this.processConditionalIPBoardToDoItems(
        ipBoard,
        wrapper,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        placementFloor
      );
    }
  }

  private static processSignIPBoardPart(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper
  ): void {
    const typeAndMeasurements = IPBoardHelper.getTypeAndMeasurements(
      ipBoard,
      ' / '
    );
    wrapper.typeAndMeasurements = StringHelper.join(
      [
        typeAndMeasurements,
        ipBoard.boardPart.sign.title,
        ipBoard.boardPart.sign.phoneNumber,
      ],
      ' - '
    );
    wrapper.signInstallerInfo = ipBoard.boardPart.sign.installerInfo;
    this.processSignIPBoardPartTitle(ipBoard, wrapper);
    this.processSignIPBoardPartPhoneNumber(ipBoard, wrapper);
  }

  private static processSignIPBoardPartTitle(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper
  ): void {
    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      if (ipBoard.boardPart.titleAdjustment != null) {
        wrapper.titleDisplay = ipBoard.boardPart.titleAdjustment.title;
        wrapper.hasTitleAdjustment = true;
      } else if (!StringHelper.isEmpty(ipBoard.boardPart.otherTitle)) {
        wrapper.titleDisplay = ipBoard.boardPart.otherTitle;
        wrapper.hasTitleAdjustment = true;
      } else {
        wrapper.titleDisplay = ipBoard.boardPart.sign.title;
        wrapper.hasTitleAdjustment = false;
      }

      if (ipBoard.boardPart.otherTitleAdjustment != null) {
        wrapper.otherSideTitleDisplay =
          ipBoard.boardPart.otherTitleAdjustment.title;
        wrapper.hasOtherSideTitleAdjustment = true;
      } else if (!StringHelper.isEmpty(ipBoard.boardPart.otherSideOtherTitle)) {
        wrapper.otherSideTitleDisplay = ipBoard.boardPart.otherSideOtherTitle;
        wrapper.hasOtherSideTitleAdjustment = true;
      } else {
        wrapper.otherSideTitleDisplay = ipBoard.boardPart.sign.title;
        wrapper.hasOtherSideTitleAdjustment = false;
      }

      if (wrapper.titleDisplay === wrapper.otherSideTitleDisplay) {
        wrapper.otherSideTitleDisplay = null;
        wrapper.hasOtherSideTitleAdjustment = false;
      }
    } else {
      this.processSignIPBoardLeftRight(
        ipBoard,
        wrapper,
        'title',
        'titleAdjustment',
        'title',
        'otherTitle',
        'titleDisplay',
        'otherSideTitleDisplay',
        'hasTitleAdjustment',
        'hasOtherSideTitleAdjustment'
      );
    }
  }

  private static processSignIPBoardPartPhoneNumber(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper
  ): void {
    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      if (ipBoard.boardPart.phoneNumberAdjustment != null) {
        wrapper.phoneNumberDisplay =
          ipBoard.boardPart.phoneNumberAdjustment.phoneNumber;
        wrapper.hasPhoneNumberAdjustment = true;
      } else if (!StringHelper.isEmpty(ipBoard.boardPart.otherPhoneNumber)) {
        wrapper.phoneNumberDisplay = ipBoard.boardPart.otherPhoneNumber;
        wrapper.hasPhoneNumberAdjustment = true;
      } else {
        wrapper.phoneNumberDisplay = ipBoard.boardPart.sign.phoneNumber;
        wrapper.hasPhoneNumberAdjustment = false;
      }

      if (ipBoard.boardPart.otherPhoneNumberAdjustment != null) {
        wrapper.otherSidePhoneNumberDisplay =
          ipBoard.boardPart.otherPhoneNumberAdjustment.phoneNumber;
        wrapper.hasOtherSidePhoneNumberAdjustment = true;
      } else if (
        !StringHelper.isEmpty(ipBoard.boardPart.otherSideOtherPhoneNumber)
      ) {
        wrapper.otherSidePhoneNumberDisplay =
          ipBoard.boardPart.otherSideOtherPhoneNumber;
        wrapper.hasOtherSidePhoneNumberAdjustment = true;
      } else {
        wrapper.otherSidePhoneNumberDisplay =
          ipBoard.boardPart.sign.phoneNumber;
        wrapper.hasOtherSidePhoneNumberAdjustment = false;
      }

      if (wrapper.phoneNumberDisplay === wrapper.otherSidePhoneNumberDisplay) {
        wrapper.otherSidePhoneNumberDisplay = null;
        wrapper.hasOtherSidePhoneNumberAdjustment = false;
      }
    } else {
      this.processSignIPBoardLeftRight(
        ipBoard,
        wrapper,
        'phoneNumber',
        'phoneNumberAdjustment',
        'phoneNumber',
        'otherPhoneNumber',
        'phoneNumberDisplay',
        'otherSidePhoneNumberDisplay',
        'hasPhoneNumberAdjustment',
        'hasOtherSidePhoneNumberAdjustment'
      );
    }
  }

  private static processSignIPBoardLeftRight(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    signValueProp: KeysOfType<SignDetail, string>,
    associationProp: KeysOfType<IPBoardPart, SignAssociation>,
    associationTextProp: KeysOfType<SignAssociation, string>,
    alternativeProp: KeysOfType<IPBoardPart, string>,
    displayProp: KeysOfType<TourItemDetailProductWrapper, string>,
    otherDisplayProp: KeysOfType<TourItemDetailProductWrapper, string>,
    hasAdjustmentProp: KeysOfType<TourItemDetailProductWrapper, boolean>,
    hasOtherAdjustmentProp: KeysOfType<TourItemDetailProductWrapper, boolean>
  ): void {
    this.processSignIPBoardLeftRightSide(
      ipBoard.boardPart,
      wrapper,
      signValueProp,
      associationProp,
      associationTextProp,
      alternativeProp,
      displayProp,
      hasAdjustmentProp
    );
    this.processSignIPBoardLeftRightSide(
      ipBoard.otherSideBoardPart,
      wrapper,
      signValueProp,
      associationProp,
      associationTextProp,
      alternativeProp,
      otherDisplayProp,
      hasOtherAdjustmentProp
    );

    if (wrapper[displayProp] === wrapper[otherDisplayProp]) {
      wrapper[otherDisplayProp] = null;
      wrapper[hasOtherAdjustmentProp] = false;
    }
  }

  private static processSignIPBoardLeftRightSide(
    ipBoardPart: IPBoardPart,
    wrapper: TourItemDetailProductWrapper,
    signValueProp: KeysOfType<SignDetail, string>,
    associationProp: KeysOfType<IPBoardPart, SignAssociation>,
    associationTextProp: KeysOfType<SignAssociation, string>,
    alternativeProp: KeysOfType<IPBoardPart, string>,
    displayProp: KeysOfType<TourItemDetailProductWrapper, string>,
    hasAdjustmentProp: KeysOfType<TourItemDetailProductWrapper, boolean>
  ): void {
    if (ipBoardPart[associationProp] != null) {
      wrapper[displayProp] = ipBoardPart[associationProp][associationTextProp];
      wrapper[hasAdjustmentProp] = true;
    } else if (!StringHelper.isEmpty(ipBoardPart[alternativeProp])) {
      wrapper[displayProp] = ipBoardPart[alternativeProp];
      wrapper[hasAdjustmentProp] = true;
    } else if (ipBoardPart.sign != null) {
      wrapper[displayProp] = ipBoardPart.sign[signValueProp];
      wrapper[hasAdjustmentProp] = false;
    }
  }

  private static processCustomIPBoardPart(
    ipBoard: IPBoard,
    collaborative: boolean,
    wrapper: TourItemDetailProductWrapper
  ): void {
    wrapper.typeAndMeasurements = IPBoardHelper.getTypeAndMeasurements(
      ipBoard,
      ' / '
    );
    if (!ipBoard.leftRightBoard || ipBoard.otherSideBoardPart == null) {
      this.doProcessCustomIPBoardPart(
        ipBoard.boardPart,
        ipBoard.boardPart,
        collaborative,
        wrapper,
        'otherTitle',
        'otherSideOtherTitle',
        'otherPhoneNumber',
        'otherSideOtherPhoneNumber'
      );
    } else {
      this.doProcessCustomIPBoardPart(
        ipBoard.boardPart,
        ipBoard.otherSideBoardPart,
        collaborative,
        wrapper,
        'otherTitle',
        'otherTitle',
        'otherPhoneNumber',
        'otherPhoneNumber'
      );
    }
  }

  private static doProcessCustomIPBoardPart(
    oneSideBoardPart: IPBoardPart,
    otherSideBoardPart: IPBoardPart,
    collaborative: boolean,
    wrapper: TourItemDetailProductWrapper,
    oneSideTitleProp: KeysOfType<IPBoardPart, string>,
    otherSideTitleProp: KeysOfType<IPBoardPart, string>,
    oneSidePhoneNumberProp: KeysOfType<IPBoardPart, string>,
    otherSidePhoneNumberProp: KeysOfType<IPBoardPart, string>
  ): void {
    wrapper.titleDisplay = oneSideBoardPart[oneSideTitleProp];
    wrapper.otherSideTitleDisplay = otherSideBoardPart[otherSideTitleProp];

    if (wrapper.titleDisplay === wrapper.otherSideTitleDisplay) {
      wrapper.otherSideTitleDisplay = null;
    }

    if (!collaborative) {
      wrapper.phoneNumberDisplay = oneSideBoardPart[oneSidePhoneNumberProp];
      wrapper.otherSidePhoneNumberDisplay =
        otherSideBoardPart[otherSidePhoneNumberProp];

      if (wrapper.phoneNumberDisplay === wrapper.otherSidePhoneNumberDisplay) {
        wrapper.otherSidePhoneNumberDisplay = null;
      }
    }
  }

  private static processIPBoardPartPlacementType(
    baseProject: boolean,
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    wrapper.placementTypeDisplay = IPBoardHelper.getShortDescription(
      ipBoard,
      ' op '
    );
    if (baseProject) {
      wrapper.placed = {
        label: wrapper.placementTypeDisplay,
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.placed,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'placed'
      );
      if (ipBoard.extraBoard != null) {
        wrapper.extraBoard = {
          label: ipBoard.extraBoard.label,
          done: false,
        };
        this.fillToDoItem(
          ipBoard,
          wrapper.extraBoard,
          immoIPPlacerTourItemDetailChangeStorageHandler,
          'extraBoard'
        );
      }
    }
    wrapper.placementOption =
      ClientIPBoardAndPlacementHelper.getInitialSelectedPlacementOption(
        ipBoard.boardPart
      );
  }

  private static processConditionalIPBoardToDoItems(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    placementFloor: string
  ): void {
    if (ipBoard.boardPart == null) {
      return;
    }
    if (!StringHelper.isEmpty(wrapper.poleColorDisplay)) {
      wrapper.poleColor = {
        label: `${wrapper.poleColorDisplay} hout`,
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.poleColor,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'poleColor'
      );
    }
    if (
      !StringHelper.isEmpty(ipBoard.boardPart.customText) ||
      !StringHelper.isEmpty(ipBoard.boardPart.otherCustomText)
    ) {
      wrapper.text = {
        label: 'Tekst controle',
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.text,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'text'
      );
    }
    if (
      ipBoard.boardPart.placementType != null &&
      ipBoard.boardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.STRETCHED.ordinal
    ) {
      wrapper.safetyNut = {
        label: 'Veiligheidsmoeren gebruikt',
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.safetyNut,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'safetyNut'
      );
    }
    this.processConditionalIPBoardAdjustmentToDoItems(
      ipBoard,
      wrapper,
      immoIPPlacerTourItemDetailChangeStorageHandler
    );
    this.processConditionalIPBoardStickerToDoItem(
      ipBoard,
      wrapper,
      immoIPPlacerTourItemDetailChangeStorageHandler
    );
    const floorHigher =
      ProjectHelper.mustHaveAppointmentForPlacementFloor(placementFloor);
    if (
      floorHigher &&
      ipBoard.boardPart.placementType !== null &&
      [
        IPBoardPlacementTypeChoices.STRETCHED.ordinal,
        IPBoardPlacementTypeChoices.BALCONY.ordinal,
      ].includes(ipBoard.boardPart.placementType.ordinal)
    ) {
      wrapper.safetyRibbon = {
        label: 'Veiligheidslint gemonteerd',
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.safetyRibbon,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'safetyRibbon'
      );
    }

    const ipBoardPlacement = <IPBoardPlacement>ipBoard.productPlacement;
    if (ipBoardPlacement !== null && ipBoardPlacement.extraHeight) {
      wrapper.extraHeight = {
        label: 'Extra materiaal',
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.extraHeight,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'extraHeight'
      );
    }
    if (
      ipBoard.boardPart.sign &&
      ipBoard.boardPart.sign.installerValue &&
      !StringHelper.isEmpty(ipBoard.boardPart.sign.installerInfo)
    ) {
      wrapper.installerInfo = {
        label: `!! Belangrijke info installateur`,
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.installerInfo,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'installerInfo'
      );
    }
  }

  private static processConditionalIPBoardAdjustmentToDoItems(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ) {
    if (ipBoard.boardPart.sign == null) {
      return;
    }

    if (wrapper.hasTitleAdjustment) {
      wrapper.leftTitle = {
        label: StringHelper.shortenText(
          `Titel${
            !StringHelper.isEmpty(wrapper.otherSideTitleDisplay) ? ' links' : ''
          }: ${wrapper.titleDisplay}`,
          30
        ),
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.leftTitle,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'leftTitle'
      );
    }
    if (wrapper.hasOtherSideTitleAdjustment) {
      wrapper.rightTitle = {
        label: StringHelper.shortenText(
          `Titel rechts: ${wrapper.otherSideTitleDisplay}`,
          30
        ),
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.rightTitle,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'rightTitle'
      );
    }

    if (wrapper.hasPhoneNumberAdjustment) {
      wrapper.leftPhoneNumber = {
        label: StringHelper.shortenText(
          `Telnr${
            !StringHelper.isEmpty(wrapper.otherSidePhoneNumberDisplay)
              ? ' links'
              : ''
          }: ${wrapper.phoneNumberDisplay}`,
          30
        ),
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.leftPhoneNumber,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'leftPhoneNumber'
      );
    }
    if (wrapper.hasOtherSidePhoneNumberAdjustment) {
      wrapper.rightPhoneNumber = {
        label: StringHelper.shortenText(
          `Telnr rechts: ${wrapper.otherSidePhoneNumberDisplay}`,
          30
        ),
        done: false,
      };
      this.fillToDoItem(
        ipBoard,
        wrapper.rightPhoneNumber,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'rightPhoneNumber'
      );
    }
  }

  private static processConditionalIPBoardStickerToDoItem(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ) {
    if (ipBoard.boardPart.sign == null) {
      return;
    }

    let stickerToDoItem: ToDoItem = null;

    if (ipBoard.boardPart.sticker != null) {
      stickerToDoItem = {
        label: `Sticker: ${ipBoard.boardPart.sticker.text}`,
        done: false,
      };
    } else if (!StringHelper.isEmpty(ipBoard.boardPart.otherSticker)) {
      stickerToDoItem = {
        label: StringHelper.shortenText(
          `Sticker: ${ipBoard.boardPart.otherSticker}`,
          30
        ),
        done: false,
      };
    }

    if (stickerToDoItem != null) {
      wrapper.sticker = stickerToDoItem;
      this.fillToDoItem(
        ipBoard,
        stickerToDoItem,
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'sticker'
      );
    }
  }

  private static processDesignAssets(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper
  ): void {
    const extraDesignAssets = CollectionHelper.isPopulated(
      ipBoard.productAssets
    )
      ? ipBoard.productAssets.filter(
          (pa) => pa.asset != null && pa.formSection === 'extraDesign'
        )
      : null;

    if (CollectionHelper.isPopulated(extraDesignAssets)) {
      wrapper.designAssets = extraDesignAssets.map((a) =>
        this.mapProductAsset(a)
      );
    } else if (ipBoard.boardPart != null && ipBoard.boardPart.sign != null) {
      const examples = IPBoardAssetHelper.getBoardPartSignExamples(ipBoard);
      if (CollectionHelper.isPopulated(examples)) {
        wrapper.designAssets = examples.map((e) =>
          this.mapSignAssociationAsset(e)
        );
      }
    }
  }

  private static mapProductAsset(
    productAsset: ProductAsset
  ): TourItemDetailAsset {
    return {
      uuid: productAsset.asset.uuid,
      backgroundImageStyle: ClientSignSelectorHelper.getBackgroundImageStyle(
        `/api/assets/${productAsset.asset.uuid}`
      ),
    };
  }

  private static mapSignAssociationAsset(
    signAssociationAsset: SignAssociationAsset
  ): TourItemDetailAsset {
    return {
      uuid: signAssociationAsset.asset.uuid,
      backgroundImageStyle: ClientSignSelectorHelper.getBackgroundImageStyle(
        `/api/assets/${signAssociationAsset.asset.uuid}`
      ),
    };
  }

  private static processPoleColor(
    ipBoard: IPBoard,
    wrapper: TourItemDetailProductWrapper
  ): void {
    if (
      ipBoard.boardPart.sign != null &&
      ipBoard.boardPart.sign.poleColor != null
    ) {
      wrapper.poleColorDisplay = ipBoard.boardPart.sign.poleColor.label;
    } else {
      wrapper.poleColorDisplay =
        ipBoard.poleColor == null ? null : ipBoard.poleColor.label;
    }
  }

  static getSitePhotos(subProject: SubProject): TourItemDetailAsset[] {
    if (
      subProject == null ||
      subProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      )
    ) {
      return null;
    }

    const toReturn: TourItemDetailAsset[] =
      subProject.projectPlacement.projectPlacementAssets.map((pa) => {
        if (pa.asset === null) {
          return null;
        }
        return this.mapProductAsset(pa);
      });
    CollectionHelper.filterInPlace(toReturn, (a) => a != null);
    return toReturn;
  }

  static getExecutionPhotos(subProject: SubProject): TourItemDetailAsset[] {
    if (
      subProject == null ||
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return null;
    }

    const toReturn: TourItemDetailAsset[] = [];

    subProject.project.products.forEach((p) => {
      if (!CollectionHelper.isPopulated(p.productAssets)) {
        return;
      }
      p.productAssets.forEach((pa) => {
        if (pa.formSection === 'execution') {
          toReturn.push(this.mapProductAsset(pa));
        }
      });
    });

    return toReturn;
  }

  static getSoldRentedStickerInfo(
    soldRented: SoldRented
  ): TourItemDetailSoldRentedStickerInfo {
    const toReturn: TourItemDetailSoldRentedStickerInfo = {};

    toReturn.amount = soldRented.amountDesign;

    if (soldRented.signSticker !== null) {
      toReturn.stickerInfo = IPBoardHelper.getSignStickerSummary(
        soldRented.signSticker
      );
      toReturn.installerInfo = soldRented.signSticker.installerInfo;
    }

    return toReturn;
  }

  static getSubProjectAssets(subProject: SubProject): SubProjectAssets {
    let ipBoardExamples: Asset[] = [];
    let designAssets: Asset[] = [];
    let latestExecutionAssets: ProductAsset[] = [];
    let executionAssets: ProductAsset[] = [];
    let sitePhotos: Asset[] = [];
    let pdfAdjustmentAssets: Asset[] = [];

    if (CollectionHelper.isPopulated(subProject.latestExecutionAssets)) {
      latestExecutionAssets = subProject.latestExecutionAssets;
    }

    if (
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      subProject.project != null &&
      subProject.project.firstProduct != null
    ) {
      ipBoardExamples = [
        subProject.project.firstProduct.ipBoardLeftExample,
        subProject.project.firstProduct.ipBoardRightExample,
      ];
      CollectionHelper.filterInPlace(ipBoardExamples, (e) => e != null);
      if (
        ipBoardExamples.length === 2 &&
        ipBoardExamples[0].uuid === ipBoardExamples[1].uuid
      ) {
        ipBoardExamples.length = 1;
      }

      if (
        CollectionHelper.isPopulated(
          subProject.project.firstProduct.productAssets
        )
      ) {
        executionAssets = subProject.project.firstProduct.productAssets.filter(
          (a) => a.formSection === 'execution' && a.asset != null
        );
        designAssets = subProject.project.firstProduct.productAssets
          .filter(
            (a) =>
              a.formSection != 'execution' &&
              a.formSection != 'arrowsDesign' &&
              a.asset != null
          )
          .map((a) => a.asset);
      }
    } else if (subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (
        subProject.projectPlacement != null &&
        CollectionHelper.isPopulated(
          subProject.projectPlacement.projectPlacementAssets
        )
      ) {
        subProject.projectPlacement.projectPlacementAssets.forEach((pa) => {
          if (pa.asset == null || pa.archived) {
            return;
          }
          if (pa.formSection === 'adjustmentPlacementSuccess') {
            executionAssets.push(pa.asset);
          } else if (pa.formSection !== 'sitePhotos') {
            if (FileHelper.isPdf(pa.asset)) {
              pdfAdjustmentAssets.push(pa.asset);
            } else {
              designAssets.push(pa.asset);
            }
          }
        });
      }

      if (subProject.type === SUB_PROJECT_TYPES.SOLDRENTED) {
        if (
          (subProject as SoldRented).signSticker != null &&
          CollectionHelper.isPopulated(
            (subProject as SoldRented).signSticker.examples
          )
        ) {
          const signAssets = (subProject as SoldRented).signSticker.examples
            .filter((e) => !e.archived && e.asset != null)
            .map((a) => a.asset);
          designAssets = [...designAssets, ...signAssets];
        }
      }

      if (designAssets.length > 0) {
        designAssets[0]['backgroundImageStyle'] = {
          'background-image': `url('/api/assets/${designAssets[0].uuid}/330/225')`,
          'background-position': 'center',
          'background-repeat': 'no-repeat',
          'background-size': 'contain',
        };
      }
    }

    if (
      CollectionHelper.isPopulated(
        subProject.projectPlacement.projectPlacementAssets
      )
    ) {
      sitePhotos = subProject.projectPlacement.projectPlacementAssets
        .filter(
          (a) =>
            !a.archived && a.formSection === 'sitePhotos' && a.asset != null
        )
        .map((a) => a.asset);
    }

    return {
      ipBoardExamples,
      designAssets,
      executionAssets,
      latestExecutionAssets,
      sitePhotos,
      pdfAdjustmentAssets,
    };
  }

  static getSubProjectToDoItems(
    subProject: SubProject,
    baseProjectPlacement: ProjectPlacement,
    ipBoard: IPBoard,
    includeExtras: boolean,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): SubProjectToDoItems {
    const toReturn: SubProjectToDoItems = {};
    const ipBoardPlacement =
      ipBoard == null ? null : <IPBoardPlacement>ipBoard.productPlacement;
    const extras: ToDoItem =
      includeExtras &&
      ((baseProjectPlacement != null && baseProjectPlacement.secondBoard) ||
        (ipBoardPlacement != null && ipBoardPlacement.arrows != null))
        ? {
            label: `Extra's`,
            done: false,
          }
        : null;
    const setToDoItemExecuted =
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
        ? ipBoardPlacement != null && ipBoardPlacement.executionComplete
        : subProject.projectPlacement != null &&
          subProject.projectPlacement.placementDone;
    if (extras != null) {
      if (setToDoItemExecuted) {
        extras.done = true;
      } else {
        immoIPPlacerTourItemDetailChangeStorageHandler.fillSubProjectToDoItem(
          extras,
          'extras'
        );
      }
      toReturn.extras = extras;
    }
    if (subProject.type !== SUB_PROJECT_TYPES.BASE_PROJECT) {
      const completed: ToDoItem = {
        label: this.getCompletedToDoItemLabel(subProject.type),
        done: false,
      };
      if (setToDoItemExecuted) {
        completed.done = true;
      } else {
        immoIPPlacerTourItemDetailChangeStorageHandler.fillSubProjectToDoItem(
          completed,
          'completed'
        );
      }
      toReturn.completed = completed;
    }
    if (subProject.type === SUB_PROJECT_TYPES.SOLDRENTED) {
      const soldRented = <SoldRented>subProject;
      if (
        soldRented.signSticker != null &&
        !StringHelper.isEmpty(soldRented.signSticker.installerInfo)
      ) {
        const stickerInfo: ToDoItem = {
          label: 'Extra info sticker',
          done: false,
        };
        if (setToDoItemExecuted) {
          stickerInfo.done = true;
        } else {
          immoIPPlacerTourItemDetailChangeStorageHandler.fillSubProjectToDoItem(
            stickerInfo,
            'stickerInfo'
          );
        }
        toReturn.stickerInfo = stickerInfo;
      }
      this.processSoldRentedExtraToDoItems(
        baseProjectPlacement,
        setToDoItemExecuted,
        toReturn,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
    }
    if (subProject.type === SUB_PROJECT_TYPES.REPO) {
      this.processRepoExtraToDoItems(
        baseProjectPlacement,
        ipBoard,
        setToDoItemExecuted,
        toReturn,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
    }
    return toReturn;
  }

  private static processSoldRentedExtraToDoItems(
    baseProjectPlacement: ProjectPlacement,
    setToDoItemExecuted: boolean,
    toDoItems: SubProjectToDoItems,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (baseProjectPlacement != null) {
      if (baseProjectPlacement.secondBoard != null) {
        this.initializeToDoItem(
          toDoItems,
          setToDoItemExecuted,
          'Sticker aangebracht op tweede bord',
          immoIPPlacerTourItemDetailChangeStorageHandler,
          'secondBoard'
        );
      }
    }
  }

  private static processRepoExtraToDoItems(
    baseProjectPlacement: ProjectPlacement,
    ipBoard: IPBoard,
    setToDoItemExecuted: boolean,
    toDoItems: SubProjectToDoItems,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (ipBoard.productPlacement != null) {
      const ipBoardPlacement = <IPBoardPlacement>ipBoard.productPlacement;
      if (IPBoardHelper.hasArrows(ipBoardPlacement)) {
        this.initializeToDoItem(
          toDoItems,
          setToDoItemExecuted,
          'Pijltjes weggehaald',
          immoIPPlacerTourItemDetailChangeStorageHandler,
          'arrows'
        );
      }
    }
    if (baseProjectPlacement != null) {
      if (baseProjectPlacement.secondBoard != null) {
        this.initializeToDoItem(
          toDoItems,
          setToDoItemExecuted,
          'Tweede bord weggehaald',
          immoIPPlacerTourItemDetailChangeStorageHandler,
          'secondBoard'
        );
      }
      if (!StringHelper.isEmpty(baseProjectPlacement.otherOrderPlacementInfo)) {
        this.initializeToDoItem(
          toDoItems,
          setToDoItemExecuted,
          'Andere bestelling weggehaald',
          immoIPPlacerTourItemDetailChangeStorageHandler,
          'otherOrder'
        );
      }
    }
    if (
      ipBoard.boardPart != null &&
      ipBoard.boardPart.placementType !== null &&
      ipBoard.boardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.ATTACHED_TO_FACADE.ordinal
    ) {
      this.initializeToDoItem(
        toDoItems,
        setToDoItemExecuted,
        'Boorgaten opgevuld',
        immoIPPlacerTourItemDetailChangeStorageHandler,
        'drillHolesFilled'
      );
    }
  }

  private static initializeToDoItem(
    toDoItems: SubProjectToDoItems,
    setToDoItemExecuted: boolean,
    label: string,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    toDoItemProp: KeysOfType<SubProjectToDoItems, ToDoItem>
  ) {
    const item: ToDoItem = {
      label: label,
      done: false,
    };
    if (setToDoItemExecuted) {
      item.done = true;
    } else {
      immoIPPlacerTourItemDetailChangeStorageHandler.fillSubProjectToDoItem(
        item,
        toDoItemProp
      );
    }
    toDoItems[toDoItemProp] = item;
  }

  static getSubProjectExecutionState(
    subProject: SubProject,
    wrapper: TourItemDetailProductWrapper,
    subProjectToDoItems: SubProjectToDoItems
  ): SubProjectExecutionState {
    const executed =
      OfflineStorageTourHelper.getSubProjectExecutionState(subProject);
    const mostRecentProjectPlacementRegisteredAction =
      subProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        subProject.projectPlacement.registeredActions
      )
        ? null
        : subProject.projectPlacement.registeredActions.find(
            (ra) =>
              ra != null &&
              ra.type != null &&
              (ra.type.ordinal ===
                REGISTERED_ACTION_TYPES.START_PROJECT_EXECUTION.ordinal ||
                ra.type.ordinal ===
                  REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION.ordinal)
          );
    const placementStarted =
      mostRecentProjectPlacementRegisteredAction != null &&
      mostRecentProjectPlacementRegisteredAction.type != null &&
      mostRecentProjectPlacementRegisteredAction.type.ordinal ===
        REGISTERED_ACTION_TYPES.START_PROJECT_EXECUTION.ordinal;
    // allowExecutedArr[0]: allowExecuted, allowExecutedArr[1]: allowNotExecuted
    const allowExecutedArr =
      subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
        ? this.getBaseProjectAllowExecuted(subProject, wrapper)
        : this.getAdjustmentAllowExecuted(subProject, subProjectToDoItems);
    allowExecutedArr[0] =
      allowExecutedArr[0] &&
      (subProjectToDoItems.extras == null || subProjectToDoItems.extras.done);

    return {
      executed: executed,
      placementStarted: placementStarted,
      allowExecuted: allowExecutedArr[0],
      allowNotExecuted: allowExecutedArr[1],
    };
  }

  private static getBaseProjectAllowExecuted(
    subProject: SubProject,
    wrapper: TourItemDetailProductWrapper
  ): boolean[] {
    const toReturn = [true, true];
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return toReturn;
    }
    for (const p of subProject.project.products) {
      toReturn[0] =
        toReturn[0] &&
        CollectionHelper.isPopulated(p.productAssets) &&
        p.productAssets.some(
          (p) => !p.archived && p.formSection === 'execution'
        );
      toReturn[1] =
        toReturn[1] &&
        (p.productPlacement == null ||
          !StringHelper.isEmpty(p.productPlacement.installerFeedback));
      if (!toReturn[0] && !toReturn[1]) {
        break;
      }
    }
    ImmoIPPlacerTourItemDetailHelper.evaluateBaseProjectToDoItemStates(
      toReturn,
      wrapper
    );
    return toReturn;
  }

  private static evaluateBaseProjectToDoItemStates(
    toReturn: boolean[],
    wrapper: TourItemDetailProductWrapper
  ) {
    toReturn[0] =
      toReturn[0] && (wrapper.placed == null || wrapper.placed.done);
    toReturn[0] =
      toReturn[0] && (wrapper.immoscoop == null || wrapper.immoscoop.done);
    toReturn[0] =
      toReturn[0] && (wrapper.spotto == null || wrapper.spotto.done);
    toReturn[0] =
      toReturn[0] && (wrapper.poleColor == null || wrapper.poleColor.done);
    toReturn[0] = toReturn[0] && (wrapper.text == null || wrapper.text.done);
    toReturn[0] =
      toReturn[0] && (wrapper.sticker == null || wrapper.sticker.done);
    toReturn[0] =
      toReturn[0] && (wrapper.leftTitle == null || wrapper.leftTitle.done);
    toReturn[0] =
      toReturn[0] && (wrapper.rightTitle == null || wrapper.rightTitle.done);
    toReturn[0] =
      toReturn[0] &&
      (wrapper.leftPhoneNumber == null || wrapper.leftPhoneNumber.done);
    toReturn[0] =
      toReturn[0] &&
      (wrapper.rightPhoneNumber == null || wrapper.rightPhoneNumber.done);
    toReturn[0] =
      toReturn[0] && (wrapper.extraBoard == null || wrapper.extraBoard.done);
    toReturn[0] =
      toReturn[0] && (wrapper.safetyNut == null || wrapper.safetyNut.done);
    toReturn[0] =
      toReturn[0] &&
      (wrapper.safetyRibbon == null || wrapper.safetyRibbon.done);
    toReturn[0] =
      toReturn[0] &&
      (wrapper.installerInfo == null || wrapper.installerInfo.done);
    toReturn[0] =
      toReturn[0] && (wrapper.extraHeight == null || wrapper.extraHeight.done);
  }

  private static getAdjustmentAllowExecuted(
    subProject: SubProject,
    subProjectToDoItems: SubProjectToDoItems
  ): boolean[] {
    const toReturn = [
      subProjectToDoItems.completed.done &&
        (subProjectToDoItems.stickerInfo == null ||
          subProjectToDoItems.stickerInfo.done) &&
        (subProjectToDoItems.arrows == null ||
          subProjectToDoItems.arrows.done) &&
        (subProjectToDoItems.secondBoard == null ||
          subProjectToDoItems.secondBoard.done) &&
        (subProjectToDoItems.otherOrder == null ||
          subProjectToDoItems.otherOrder.done) &&
        (subProjectToDoItems.drillHolesFilled == null ||
          subProjectToDoItems.drillHolesFilled.done),
      subProject.projectPlacement == null ||
        !StringHelper.isEmpty(subProject.projectPlacement.placerFeedback),
    ];
    toReturn[0] =
      toReturn[0] &&
      (subProject.type === SUB_PROJECT_TYPES.REPO ||
        subProject.projectPlacement == null ||
        (CollectionHelper.isPopulated(
          subProject.projectPlacement.projectPlacementAssets
        ) &&
          subProject.projectPlacement.projectPlacementAssets.some(
            (pa) =>
              !pa.archived && pa.formSection === 'adjustmentPlacementSuccess'
          )));
    return toReturn;
  }

  private static getCompletedToDoItemLabel(type: string): string {
    switch (type) {
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        return 'Aanpassing klaar';
      case SUB_PROJECT_TYPES.SOLDRENTED:
        return 'Sticker aangebracht';
      case SUB_PROJECT_TYPES.REPO:
        return 'Bord weggehaald';
      default:
        throw new Error(`invalid type: ${type}`);
    }
  }

  static handleStartPlacement(
    subProject: SubProject,
    user: User,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (subProject.projectPlacement != null) {
      if (subProject.projectPlacement.registeredActions == null) {
        subProject.projectPlacement.registeredActions = [];
      }
      const newRegisteredAction = GeneralHelper.initializeRegisteredAction(
        user,
        new Date(),
        REGISTERED_ACTION_TYPES.START_PROJECT_EXECUTION,
        true
      );
      subProject.projectPlacement.registeredActions.unshift(
        newRegisteredAction
      );
      immoIPPlacerTourItemDetailChangeStorageHandler.addRegisteredAction(
        newRegisteredAction
      );
      immoIPPlacerTourItemDetailChangeStorageHandler.submitRegisteredActions();
    }
  }

  static handleSubmitExecuted(
    subProject: SubProject,
    user: User,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<void> {
    const executedDate = new Date();
    subProject.currentExecutionDate = executedDate;
    const baseProject = subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT;
    if (baseProject) {
      this.handleBaseProjectExecuted(
        subProject,
        executedDate,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
    } else {
      this.handleAdjustmentExecuted(subProject);
    }
    const registeredAction = GeneralHelper.initializeRegisteredAction(
      user,
      new Date(),
      REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION,
      true
    );
    if (subProject.projectPlacement != null) {
      if (subProject.projectPlacement.registeredActions == null) {
        subProject.projectPlacement.registeredActions = [];
      }
      subProject.projectPlacement.registeredActions.unshift(registeredAction);
    }
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      {
        baseProject: baseProject,
        date: executedDate,
        registeredAction: registeredAction,
      },
      immoIPPlacerTourItemDetailChangeStorageHandler.setSubProjectExecuted
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitSubProject(
      true
    );
  }

  private static handleBaseProjectExecuted(
    subProject: SubProject,
    executionDate: Date,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return;
    }
    const executionTime = DateHelper.formatTimeOnly(executionDate);
    const executionParameterObject: ProductExecutedParameterObject = {
      executionDate: executionDate,
      executionTime: executionTime,
    };
    subProject.project.products.forEach((p) => {
      if (p.productPlacement == null) {
        p.productPlacement.executionComplete = true;
        p.productPlacement.executionDate = executionDate;
        p.productPlacement.executionTime = executionTime;
      }
      immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
        p,
        executionParameterObject,
        immoIPPlacerTourItemDetailChangeStorageHandler.setProductExecuted
      );
    });
  }

  private static handleAdjustmentExecuted(subProject: SubProject): void {
    if (subProject.projectPlacement == null) {
      return;
    }
    subProject.projectPlacement.placementDone = true;
  }

  static handleSubmitNotExecuted(
    subProject: SubProject,
    user: User,
    notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<void> {
    subProject.currentExecutionDate = null;
    const baseProject = subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT;
    if (baseProject) {
      this.handleBaseProjectNotExecuted(
        subProject,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
    } else {
      this.handleAdjustmentNotExecuted(subProject);
    }
    const registeredAction = GeneralHelper.initializeRegisteredAction(
      user,
      new Date(),
      REGISTERED_ACTION_TYPES.STOP_PROJECT_EXECUTION,
      true
    );
    if (subProject.projectPlacement != null) {
      if (subProject.projectPlacement.registeredActions == null) {
        subProject.projectPlacement.registeredActions = [];
      }
      subProject.projectPlacement.registeredActions.unshift(registeredAction);
    }
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      {
        baseProject: baseProject,
        registeredAction: registeredAction,
        notExecutedHistoryItemObject: notExecutedHistoryItemObject,
      },
      immoIPPlacerTourItemDetailChangeStorageHandler.setSubProjectNotExecuted
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitSubProject(
      false
    );
  }

  private static handleBaseProjectNotExecuted(
    subProject: SubProject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return;
    }
    subProject.project.products.forEach((p) => {
      if (p.productPlacement == null) {
        p.productPlacement.executionComplete = null;
        p.productPlacement.executionDate = null;
        p.productPlacement.executionTime = null;
      }
      immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
        p,
        null,
        immoIPPlacerTourItemDetailChangeStorageHandler.setProductNotExecuted
      );
    });
  }

  private static handleAdjustmentNotExecuted(subProject: SubProject): void {
    if (subProject.projectPlacement == null) {
      return;
    }
    subProject.projectPlacement.placementDone = false;
  }

  static handleReset(
    subProject: SubProject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    executed: boolean
  ): Promise<void> {
    subProject.currentExecutionDate = null;
    const baseProject = subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT;

    if (baseProject) {
      this.handleResetBaseProject(
        subProject,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
    } else {
      this.handleResetAdjustment(subProject);
    }

    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      {
        baseProject: baseProject,
      },
      immoIPPlacerTourItemDetailChangeStorageHandler.reset
    );
    if (executed) {
      subProject.status = 'Niet_uitgevoerd';
    }
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitReset(executed);
  }

  private static handleResetBaseProject(
    subProject: SubProject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return;
    }
    subProject.project.products.forEach((p) => {
      if (p.productPlacement != null) {
        p.productPlacement.executionComplete = null;
        p.productPlacement.executionDate = null;
        p.productPlacement.executionTime = null;
      }
      immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineProductChange(
        p,
        null,
        immoIPPlacerTourItemDetailChangeStorageHandler.resetProductNotExecuted
      );
    });
  }

  private static handleResetAdjustment(subProject: SubProject): void {
    if (subProject.projectPlacement != null) {
      subProject.projectPlacement.placementDone = null;
    }
  }

  static handleExecutionAssetAdded(
    subProject: SubProject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    const executed =
      OfflineStorageTourHelper.getSubProjectExecutionState(subProject);
    if (executed != null) {
      immoIPPlacerTourItemDetailChangeStorageHandler.directlySubmitExecutionAssets();
    }
  }

  static handleNotExecutedAssetAdded(
    subProject: SubProject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): void {
    const executed =
      OfflineStorageTourHelper.getSubProjectExecutionState(subProject);
    if (executed != null) {
      immoIPPlacerTourItemDetailChangeStorageHandler.directlySubmitNotExecutedAssets();
    }
  }

  static async handleSubProjectChanged(
    shownSubProject: SubProject,
    changedSubProject: SubProject,
    wrapper: TourItemDetailProductWrapper,
    changeType: ChangeType,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<void> {
    switch (changeType) {
      case ChangeType.SUBMIT:
        shownSubProject.projectPlacement = changedSubProject.projectPlacement;
        if (
          shownSubProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
          changedSubProject.project != null &&
          CollectionHelper.isPopulated(changedSubProject.project.products)
        ) {
          wrapper.ipBoard = <IPBoard>changedSubProject.project.products[0];
          if (shownSubProject.project != null) {
            shownSubProject.project.products =
              changedSubProject.project.products;
          }
        }
        await immoIPPlacerTourItemDetailChangeStorageHandler.resetRegisteredActions(
          true
        );
        await immoIPPlacerTourItemDetailChangeStorageHandler.reloadOfflineChangeObjects(
          shownSubProject
        );
        break;
      case ChangeType.REGISTERED_ACTIONS:
        if (
          shownSubProject.projectPlacement != null &&
          changedSubProject.projectPlacement != null
        ) {
          shownSubProject.projectPlacement.registeredActions =
            changedSubProject.projectPlacement.registeredActions;
        }
        immoIPPlacerTourItemDetailChangeStorageHandler.resetRegisteredActions();
        break;
      case ChangeType.ASSETS:
        if (shownSubProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
          if (
            shownSubProject.project != null &&
            CollectionHelper.isPopulated(shownSubProject.project.products) &&
            changedSubProject.project != null &&
            CollectionHelper.isPopulated(changedSubProject.project.products)
          ) {
            shownSubProject.project.products[0].productAssets =
              changedSubProject.project.products[0].productAssets;
          }
        } else if (
          shownSubProject.projectPlacement != null &&
          changedSubProject.projectPlacement != null
        ) {
          shownSubProject.projectPlacement.projectPlacementAssets =
            changedSubProject.projectPlacement.projectPlacementAssets;
        }
        immoIPPlacerTourItemDetailChangeStorageHandler.reloadOfflineChangeObjects(
          shownSubProject
        );
        break;
      case ChangeType.MODIFY_CONFIG:
        // no op
        break;
    }
  }

  static handleSubmitChanges(
    notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<void> {
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      {
        notExecutedHistoryItemObject: notExecutedHistoryItemObject,
      },
      immoIPPlacerTourItemDetailChangeStorageHandler.setSubProjectChanges
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitSubProject(
      false
    );
  }

  static handleSubmitInternalInfo(
    value: { info: string; recurring: boolean },
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<void> {
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      value,
      immoIPPlacerTourItemDetailChangeStorageHandler.changeInternalInfo
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitInternalInfo();
  }

  static handleSubmitPlacementConfig(
    boardType: BoardType,
    placementType: PlacementType,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<SubProject> {
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      { boardType, placementType },
      immoIPPlacerTourItemDetailChangeStorageHandler.changePlacementConfig
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitPlacementConfig();
  }

  static async handleChangeAddress(
    subProject: SubProject,
    address: Address,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler,
    immoIPChangesService: ImmoIPChangesService
  ): Promise<void> {
    try {
      await this.handleSubmitAddress(
        address,
        immoIPPlacerTourItemDetailChangeStorageHandler
      );
      immoIPChangesService.submitAddressSavedSuccessfully(
        subProject.id,
        address
      );
    } catch (err) {
      if (ErrorHelper.isConnectionLostError(err)) {
        immoIPPlacerTourItemDetailChangeStorageHandler.applyModifiedConfigToSubProject(
          subProject
        );
        this.prepareForReloadDueToModifiedConfig(subProject);
        immoIPChangesService.notifyAddressSaveError(
          subProject.id,
          err,
          address
        );
      } else {
        immoIPChangesService.notifyAddressSaveError(subProject.id, err);
      }
    }
  }

  private static handleSubmitAddress(
    address: Address,
    immoIPPlacerTourItemDetailChangeStorageHandler: ImmoIPPlacerTourItemDetailChangeStorageHandler
  ): Promise<SubProject> {
    immoIPPlacerTourItemDetailChangeStorageHandler.storeOfflineSubProjectChange(
      { address: address },
      immoIPPlacerTourItemDetailChangeStorageHandler.changeAddress
    );
    return immoIPPlacerTourItemDetailChangeStorageHandler.submitAddress();
  }

  static prepareForReloadDueToModifiedConfig(subProject: SubProject): void {
    if (subProject.projectPlacement != null) {
      if (subProject.projectPlacement.projectPlacementAssets != null) {
        CollectionHelper.filterInPlace(
          subProject.projectPlacement.projectPlacementAssets,
          (pa) => pa.id != null
        );
      }
      if (subProject.projectPlacement.registeredActions != null) {
        CollectionHelper.filterInPlace(
          subProject.projectPlacement.registeredActions,
          (ra) => ra.id != null
        );
      }
    }
    if (subProject.project != null && subProject.project.products != null) {
      for (const product of subProject.project.products) {
        if (product.productAssets != null) {
          CollectionHelper.filterInPlace(
            product.productAssets,
            (pa) => pa.id != null
          );
        }
      }
    }
  }
}

export interface AppointmentInfo {
  appointmentTimeOfDay: string;
  appointmentContactDisplay: string;
  needAppointmentInfo: string;
  appointmentReady: boolean;
  appointmentFailure: boolean;
  appointmentRegistration: string;
  appointmentWithCustomer: boolean;
  collectKeyAddress: string;
}

export interface SubProjectToDoItems {
  completed?: ToDoItem;
  stickerInfo?: ToDoItem;
  extras?: ToDoItem;
  arrows?: ToDoItem;
  secondBoard?: ToDoItem;
  otherOrder?: ToDoItem;
  drillHolesFilled?: ToDoItem;
}

export interface SubProjectExecutionState {
  executed: boolean;
  placementStarted: boolean;
  allowExecuted: boolean;
  allowNotExecuted: boolean;
}

export interface SubProjectAssets {
  ipBoardExamples: Asset[];
  designAssets: Asset[];
  executionAssets: ProductAsset[];
  latestExecutionAssets: ProductAsset[];
  sitePhotos: Asset[];
  pdfAdjustmentAssets: Asset[];
}
